import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faExclamationCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import {
  faDiamondExclamation,
  faFireFlame
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GlobalPropertiesSelect,
  PropertiesStore
} from '@hortplus/properties-react';
import isMobile from 'ismobilejs';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  Collapse,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip
} from 'react-bootstrap';
import InsectGroupSelect from './InsectGroupSelect';
import TraplogLegend from './TraplogLegend';
import { calcAccumulationThreshold } from './helpers/TrappingUtils';
import TrappingZoneStatusModal from './helpers/TrappingZoneStatusModal';
import useLocalStorageState from './helpers/useLocalStorageState';
import { useCollapse } from './hooks/useCollapse';
import { useTrappingZoneThresholds } from './hooks/useTrappingZoneThresholds';
import { useUpdateTrappingZoneHighPressure } from './hooks/useUpdateTrappingZoneHighPressure';
import PropertyOverviewMap from './maps/PropertyOverviewMap';
import { useStores } from './stores/useStores';

const HighRiskAlert = () => {
  return (
    <Alert variant='warning'>
      <div>
        <FontAwesomeIcon
          className='d-inline mr-2'
          icon={faDiamondExclamation}
        />
        <p className='d-inline'>
          One or more sites within your property have been classified as High
          Risk.{' '}
          <TrappingZoneStatusModal
            title={'High Risk Sites:'}
            body={
              <>
                <div>
                  <p style={{ fontSize: '18px' }}>
                    A production site becomes high-risk if:
                  </p>
                </div>
                <div>
                  <ol>
                    <li className='mb-3'>
                      CM was detected at harvest or post-harvest in the previous
                      season, or
                    </li>
                    <li className='mb-3'>
                      Production site was removed from CMSM programme in the
                      previous season due to non-compliance with this programme,
                      or
                    </li>
                    <li>
                      Production site is managed by growers new to this
                      programme and who does not have a documented history from
                      within the last 2 seasons of CM management.
                    </li>
                  </ol>
                </div>
              </>
            }
          >
            <a href='javascript:;'>Click here to learn more.</a>
          </TrappingZoneStatusModal>
        </p>
      </div>
    </Alert>
  );
};

const HighPressureAlert = () => {
  const [show, setShow] = useLocalStorageState(
    'traplog-client-high-pressure-alert-show',
    true
  );

  if (!show) return <></>;
  return (
    <Alert variant='info' onClose={() => setShow(false)} dismissible>
      <div>
        <FontAwesomeIcon className='d-inline mr-2' icon={faFireFlame} />
        <p className='d-inline'>
          One or more sites within your property have been classified as High
          pressure. Notifying pack-houses and bin assessments is advised:{' '}
          <TrappingZoneStatusModal
            title={'High Risk Sites:'}
            body={
              <p>
                Your production site's CM traps exceeded the thresholds on four
                occasions and 5 or more chemical interventions for CM have been
                made so far into the season. Please let your pack-house know and
                discuss with them any additional checks they may wish done to
                ensure the fruit is still suitable for the CMSM market.
              </p>
            }
          >
            <a href='javascript:;'>More Info.</a>
          </TrappingZoneStatusModal>
        </p>
      </div>
    </Alert>
  );
};

const TrappingZone = ({ trappingZone }) => {
  const { traplogStore } = useStores();
  const { calculateThresholds } = useTrappingZoneThresholds();
  const { updateTrappingZoneHighPressure } =
    useUpdateTrappingZoneHighPressure();

  const trappingZoneThresholds = useCallback(
    (trappingZone, selectedInsectGroup) => {
      const trappingZoneId = trappingZone.id;
      traplogStore.trappingResults[trappingZoneId]?.results.forEach(
        (trappingResult) => {
          const {
            amountThresholdsMet,
            accumulationThresholdsMet,
            averageThresholdMet
          } = calculateThresholds(
            trappingZone,
            trappingResult,
            selectedInsectGroup
          );
          // Counts the thresholds (ignoring sprayed) and
          // sets the current trappingZone.high_pressure property
          // to true of the minimum number of thresholds is reached.
          updateTrappingZoneHighPressure(
            trappingZone,
            trappingResult,
            amountThresholdsMet,
            accumulationThresholdsMet,
            averageThresholdMet()
          );
        }
      );
    },
    [
      traplogStore.trappingResultscalculateThresholds,
      updateTrappingZoneHighPressure
    ]
  );

  useEffect(() => {
    trappingZoneThresholds(trappingZone, traplogStore.selectedInsectGroup);
  }, [trappingZoneThresholds, traplogStore.selectedInsectGroup]);

  const variantColour =
    traplogStore.trappingResults &&
    traplogStore.trappingResults[trappingZone.id]
      ? traplogStore.trappingResults[trappingZone.id].overview.status ===
          'good' ||
        traplogStore.trappingResults[trappingZone.id].overview.status ===
          'sprayed'
        ? 'success'
        : traplogStore.trappingResults[trappingZone.id].overview.status ===
          'no_data'
        ? 'secondary'
        : traplogStore.trappingResults[trappingZone.id].overview.status ===
            'pre_trap_catch_threshold_met' ||
          traplogStore.trappingResults[trappingZone.id].overview.status ===
            'accumulation_threshold_met' ||
          traplogStore.trappingResults[trappingZone.id].overview.status ===
            'average_threshold_met'
        ? 'danger'
        : traplogStore.trappingResults[trappingZone.id].overview.status ===
          'near_accumulation_threshold'
        ? 'warning'
        : 'primary'
      : 'primary';
  return (
    <Button
      key={trappingZone.id}
      className='mb-3 mx-2'
      variant={`${
        traplogStore.selectedTrappingZoneId === trappingZone.id
          ? ''
          : 'outline-'
      }${variantColour}`}
      onClick={() => traplogStore.setSelectedTrappingZoneId(trappingZone.id)}
    >
      {`${trappingZone.name}`}
      {traplogStore.isTrappingZoneHighRisk(trappingZone) ? (
        <FontAwesomeIcon className='ml-1' icon={faDiamondExclamation} />
      ) : null}
      {traplogStore.isTrappingZoneHighPressure(trappingZone) ? (
        <FontAwesomeIcon className='ml-1' icon={faFireFlame} />
      ) : null}
      {`${trappingZone.cmsm ? ' (CMSM)' : ''}`}
    </Button>
  );
};

const TrappingResultsTable = () => {
  const { traplogStore } = useStores();

  const columns = [
    { title: 'Date' },
    { title: 'Week' },
    { title: 'Cap' },
    { title: 'Base' },
    { title: 'TH' },
    ...traplogStore.selectedTrappingZone.traps
      .filter((trap) =>
        trap.trap_lure.insects.some(
          (insect) =>
            insect.insect_group.id === traplogStore.selectedInsectGroupId
        )
      )
      .map((trap) => ({
        title: trap.name,
        trapId: trap.id,
        trapName: trap.name,
        colSpan: 2
      })),
    {
      title: (
        <>
          Avg/Week{' '}
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip>
                The average number of insects per week across all traps.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} className='ml-2' size='xs' />
          </OverlayTrigger>
        </>
      )
    }
  ];

  const trappingResults = traplogStore.trappingResults[
    traplogStore.selectedTrappingZoneId
  ].results
    // Sorts the rows by earliest date first
    .slice()
    .sort((trappingResult1, trappingResult2) => {
      const m1 = moment(trappingResult1.date);
      const m2 = moment(trappingResult2.date);
      return m1.isSame(m2) ? 0 : m1.isAfter(m2) ? -1 : 1;
    });

  const tableData = useMemo(() => {
    // Helper functions
    const formatDate = (dt) => ({
      date: dt.format('DD/MM/YYYY'),
      week: dt.format('w')
    });

    const calculateChangeStatus = (changedCount, totalTraps) => {
      if (changedCount === 0) return 'none';
      return changedCount < totalTraps ? 'some' : 'all';
    };

    const calculateAverageAmount = (amounts) => {
      if (!amounts.length) return null;
      const sum = amounts.reduce((acc, val) => acc + val, 0);
      return (sum / amounts.length).toFixed(2);
    };

    const processTraps = (trappingResult, traplogStore) => {
      const amounts = [];
      let amountThresholdsMet = 0;
      let accumulationThresholdsMet = 0;

      const traps = traplogStore.traps.map((trap) => {
        const monitoringRecord = trappingResult.trap_monitoring_records.find(
          (record) => record.trap.id === trap.id
        );

        const trapData = trappingResult.traps[trap.id] || {};
        const { amount = null, accumulation = null } = trapData;

        const amountThresholdMet =
          amount &&
          traplogStore.selectedInsectGroup.per_trap_catch_threshold !== null &&
          amount >= traplogStore.selectedInsectGroup.per_trap_catch_threshold;

        const accumulationThresholdMet =
          accumulation &&
          traplogStore.selectedInsectGroup.accumulation_threshold !== null &&
          accumulation >=
            calcAccumulationThreshold(
              traplogStore.selectedInsectGroup,
              moment(trappingResult.date)
            );

        if (amountThresholdMet) amountThresholdsMet++;
        if (accumulationThresholdMet) accumulationThresholdsMet++;

        amounts.push(amount);

        return {
          name: trap.name,
          amount,
          accumulation,
          comments: monitoringRecord?.comments || null,
          amountThresholdMet,
          accumulationThresholdMet
        };
      });

      return { traps, amounts, amountThresholdsMet, accumulationThresholdsMet };
    };

    // Main processing
    const results = trappingResults.map((trappingResult) => {
      const dt = moment(trappingResult.date);
      const dateInfo = formatDate(dt);

      const { traps, amounts, amountThresholdsMet, accumulationThresholdsMet } =
        processTraps(trappingResult, traplogStore);

      const averageAmount = calculateAverageAmount(amounts);
      const averageThresholdMet =
        traplogStore.selectedInsectGroup.average_threshold !== null &&
        averageAmount >= traplogStore.selectedInsectGroup.average_threshold;

      const luresChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.lure_changed
      ).length;

      const basesChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.base_changed
      ).length;

      return {
        ...dateInfo,
        capChanges: calculateChangeStatus(luresChanged, traps.length),
        baseChanges: calculateChangeStatus(basesChanged, traps.length),
        sprays: trappingResult.trapping_zone_sprays.map((spray) => ({
          ...dateInfo,
          biofix: spray.biofix,
          product: spray.spray_product.product.name,
          comments: spray.comments
        })),
        record:
          trappingResult.trap_monitoring_records.length > 0
            ? {
                ...dateInfo,
                traps,
                luresChanged,
                basesChanged,
                amountThresholdsMet,
                accumulationThresholdsMet,
                averageThresholdMet,
                averageAmount
              }
            : null,
        sprayed: trappingResult.sprayed
      };
    });

    // Format final page data
    return results.flatMap((result) => {
      const output = [];

      if (result.sprays.length > 0) {
        output.push(
          ...result.sprays.map((spray) => ({
            date: result.date,
            week: result.week,
            spray
          }))
        );
      }

      if (result.record) {
        const trapRecords = {};
        result.record.traps.forEach((trap) => {
          const record = {
            isRecord: true,
            amount: trap.amount,
            accumulation: trap.accumulation,
            comments: trap.comments
          };
          trapRecords[trap.name] = record;
        });
        output.push({
          date: result.date,
          week: result.week,
          capChanges: result.capChanges,
          baseChanges: result.baseChanges,
          threshold: {
            sprayed: result.sprayed,
            amountThresholdsMet: result.record.amountThresholdsMet,
            accumulationThresholdsMet: result.record.accumulationThresholdsMet,
            averageThresholdMet: result.record.averageThresholdMet
          },
          ...trapRecords,
          averageAmount: result.record.averageAmount
        });
      }
      return output;
    });
  }, [trappingResults, traplogStore.traps, traplogStore.selectedInsectGroup]);

  const rowHasRecords = useCallback((row) => {
    return Object.keys(row).some((key) => {
      if (typeof row[key] === 'object' && row[key] !== null) {
        return row[key].isRecord;
      }
      return false;
    });
  }, []);

  return (
    <Table bordered striped responsive>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.trapId}
              colSpan={column.colSpan ? column.colSpan : 1}
            >
              {column.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tableData.map((row, index) => {
          const hasSpray = row.spray !== undefined;
          const hasRecords = rowHasRecords(row);
          return (
            <tr key={index}>
              <td>{row.date}</td>
              <td>{row.week}</td>
              {hasSpray ? (
                <Fragment>
                  <td colSpan={4 + 2 * traplogStore.traps.length}>
                    Spray Application{' '}
                    {<i>{row.spray.biofix === 1 && '(Biofix)'}</i>}:{' '}
                    <b>{row.spray.product}</b>
                    {row.spray.comments ? (
                      <>
                        <br />
                        <i>{row.spray.comments}</i>
                      </>
                    ) : null}
                  </td>
                </Fragment>
              ) : null}
              {hasRecords ? (
                <>
                  <td>
                    {row.capChanges === 'some' ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>Some caps changed.</Tooltip>}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className='text-warning'
                        />
                      </OverlayTrigger>
                    ) : row.capChanges === 'all' ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>All caps changed.</Tooltip>}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className='text-success'
                        />
                      </OverlayTrigger>
                    ) : null}
                  </td>
                  <td>
                    {row.baseChanges === 'some' ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>Some bases changed.</Tooltip>}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className='text-warning'
                        />
                      </OverlayTrigger>
                    ) : row.baseChanges === 'all' ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>All bases changed.</Tooltip>}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className='text-success'
                        />
                      </OverlayTrigger>
                    ) : null}
                  </td>
                  <td
                    className={
                      row.threshold.sprayed ? 'text-info' : 'text-danger'
                    }
                  >
                    {row.threshold.amountThresholdsMet > 0 ||
                    row.threshold.accumulationThresholdsMet > 0 ||
                    row.threshold.averageThresholdMet ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={
                          <Tooltip>
                            {row.threshold.sprayed ? (
                              <Row>
                                <Col>
                                  Previous spray covers these thresholds.
                                </Col>
                              </Row>
                            ) : null}
                            {row.threshold.amountThresholdsMet > 0 ? (
                              <Row>
                                <Col>
                                  {row.threshold.amountThresholdsMet} x Single
                                  trap catch threshold.
                                </Col>
                              </Row>
                            ) : null}
                            {row.threshold.accumulationThresholdsMet > 0 ? (
                              <Row>
                                <Col>
                                  {row.threshold.accumulationThresholdsMet} x
                                  accumulation threshold.
                                </Col>
                              </Row>
                            ) : null}
                            {row.threshold.averageThresholdMet ? (
                              <Row>
                                <Col>Average threshold.</Col>
                              </Row>
                            ) : null}
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      </OverlayTrigger>
                    ) : null}
                  </td>
                  {columns
                    .filter((column) => column.trapId !== undefined)
                    .map((column, index) => {
                      const record = row[column.trapName];
                      return (
                        <Fragment key={index}>
                          <td
                            className={
                              record.amountThresholdMet ? 'text-danger' : null
                            }
                          >
                            {record.amount === null ? '-' : record.amount}
                          </td>
                          <td
                            className={`${
                              record.accumulationThresholdMet
                                ? 'text-danger'
                                : null
                            } ${record.comments ? 'pr-0' : null}`}
                          >
                            <i>
                              {record.accumulation === null
                                ? '-'
                                : record.accumulation}
                            </i>
                            {record.comments ? (
                              <OverlayTrigger
                                placement='top'
                                overlay={<Tooltip>{record.comments}</Tooltip>}
                              >
                                <FontAwesomeIcon
                                  className='text-primary ml-2'
                                  icon={faComment}
                                />
                              </OverlayTrigger>
                            ) : null}
                          </td>
                        </Fragment>
                      );
                    })}
                  <td
                    className={
                      row.threshold.averageThresholdMet ? 'text-danger' : null
                    }
                  >
                    {row.averageAmount ? row.averageAmount : '-'}
                  </td>
                </>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const TrappingResultsTablePrinted = () => {
  const { traplogStore } = useStores();

  const defaultColumnsPerPage = 10;

  const columns = useMemo(
    () => [
      { title: 'Date' },
      { title: 'Week' },
      { title: 'Cap' },
      { title: 'Base' },
      { title: 'TH' },
      ...traplogStore.selectedTrappingZone.traps
        .filter((trap) =>
          trap.trap_lure.insects.some(
            (insect) =>
              insect.insect_group.id === traplogStore.selectedInsectGroupId
          )
        )
        .map((trap) => ({
          title: trap.name,
          trapId: trap.id,
          trapName: trap.name,
          colSpan: 2
        })),
      {
        title: 'Avg/Week'
      }
    ],
    [
      traplogStore.selectedTrappingZone.traps,
      traplogStore.selectedInsectGroupId
    ]
  );

  const pageColumns = useMemo(() => {
    const pageColumns = [];
    let pageColumn = [];
    columns.forEach((column) => {
      pageColumn.push(column);
      if (pageColumn.length === defaultColumnsPerPage) {
        pageColumns.push([...pageColumn]);
        pageColumn = [];
      }
    });
    // Add any remaining columns to last page
    if (pageColumn.length > 0) {
      pageColumns.push([...pageColumn]);
    }
    return pageColumns;
  }, [columns]);

  const trappingResults = traplogStore.trappingResults[
    traplogStore.selectedTrappingZoneId
  ].results
    // Sorts the rows by earliest date first.
    .slice()
    .sort((trappingResult1, trappingResult2) => {
      const m1 = moment(trappingResult1.date);
      const m2 = moment(trappingResult2.date);
      return m1.isSame(m2) ? 0 : m1.isAfter(m2) ? -1 : 1;
    });

  const tableData = useMemo(() => {
    // Helper functions
    const formatDate = (dt) => ({
      date: dt.format('DD/MM/YYYY'),
      week: dt.format('w')
    });

    const calculateChangeStatus = (changedCount, totalTraps) => {
      if (changedCount === 0) return 'none';
      return changedCount < totalTraps ? 'some' : 'all';
    };

    const calculateAverageAmount = (amounts) => {
      if (!amounts.length) return null;
      const sum = amounts.reduce((acc, val) => acc + val, 0);
      return (sum / amounts.length).toFixed(2);
    };

    const processTraps = (trappingResult, traplogStore) => {
      const amounts = [];
      let amountThresholdsMet = 0;
      let accumulationThresholdsMet = 0;

      const traps = traplogStore.traps.map((trap) => {
        const monitoringRecord = trappingResult.trap_monitoring_records.find(
          (record) => record.trap.id === trap.id
        );

        const trapData = trappingResult.traps[trap.id] || {};
        const { amount = null, accumulation = null } = trapData;

        const amountThresholdMet =
          amount &&
          traplogStore.selectedInsectGroup.per_trap_catch_threshold !== null &&
          amount >= traplogStore.selectedInsectGroup.per_trap_catch_threshold;

        const accumulationThresholdMet =
          accumulation &&
          traplogStore.selectedInsectGroup.accumulation_threshold !== null &&
          accumulation >=
            calcAccumulationThreshold(
              traplogStore.selectedInsectGroup,
              moment(trappingResult.date)
            );

        if (amountThresholdMet) amountThresholdsMet++;
        if (accumulationThresholdMet) accumulationThresholdsMet++;

        amounts.push(amount);

        return {
          name: trap.name,
          amount,
          accumulation,
          comments: monitoringRecord?.comments || null,
          amountThresholdMet,
          accumulationThresholdMet
        };
      });

      return { traps, amounts, amountThresholdsMet, accumulationThresholdsMet };
    };

    // Main processing
    const results = trappingResults.map((trappingResult) => {
      const dt = moment(trappingResult.date);
      const dateInfo = formatDate(dt);

      const { traps, amounts, amountThresholdsMet, accumulationThresholdsMet } =
        processTraps(trappingResult, traplogStore);

      const averageAmount = calculateAverageAmount(amounts);
      const averageThresholdMet =
        traplogStore.selectedInsectGroup.average_threshold !== null &&
        averageAmount >= traplogStore.selectedInsectGroup.average_threshold;

      const luresChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.lure_changed
      ).length;

      const basesChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.base_changed
      ).length;

      return {
        ...dateInfo,
        capChanges: calculateChangeStatus(luresChanged, traps.length),
        baseChanges: calculateChangeStatus(basesChanged, traps.length),
        sprays: trappingResult.trapping_zone_sprays.map((spray) => ({
          ...dateInfo,
          biofix: spray.biofix,
          product: spray.spray_product.product.name,
          comments: spray.comments
        })),
        record:
          trappingResult.trap_monitoring_records.length > 0
            ? {
                ...dateInfo,
                traps,
                luresChanged,
                basesChanged,
                amountThresholdsMet,
                accumulationThresholdsMet,
                averageThresholdMet,
                averageAmount
              }
            : null,
        sprayed: trappingResult.sprayed
      };
    });

    // Format final page data
    return results.flatMap((result) => {
      const output = [];

      if (result.sprays.length > 0) {
        output.push(
          ...result.sprays.map((spray) => ({
            date: result.date,
            week: result.week,
            spray
          }))
        );
      }

      if (result.record) {
        const trapRecords = {};
        result.record.traps.forEach((trap) => {
          const record = {
            isRecord: true,
            amount: trap.amount,
            accumulation: trap.accumulation,
            comments: trap.comments
          };
          trapRecords[trap.name] = record;
        });
        output.push({
          date: result.date,
          week: result.week,
          capChanges: result.capChanges,
          baseChanges: result.baseChanges,
          threshold: {
            sprayed: result.sprayed,
            amountThresholdsMet: result.record.amountThresholdsMet,
            accumulationThresholdsMet: result.record.accumulationThresholdsMet,
            averageThresholdMet: result.record.averageThresholdMet
          },
          ...trapRecords,
          averageAmount: {
            averageAmount: result.record.averageAmount,
            averageThresholdMet: result.record.averageThresholdMet
          }
        });
      }
      return output;
    });
  }, [trappingResults, traplogStore.traps, traplogStore.selectedInsectGroup]);

  const pageData = useMemo(() => {
    const tableDatas = [];
    let currentData = [];
    let currentRow = {};
    for (let i = 0; i < tableData.length; i++) {
      const row = tableData[i];
      const rowKeys = Object.keys(row);
      rowKeys.forEach((key, index) => {
        const value = row[key];
        currentRow[key] = value;
        if (
          Object.keys(currentRow).length === defaultColumnsPerPage ||
          rowKeys.length - 1 === index
        ) {
          currentData.push(currentRow);
          currentRow = {};
        }
      });
      tableDatas.push(currentData);
      currentData = [];
    }
    const pages = [];
    const maxLength = Math.max(...tableDatas.map((arr) => arr.length));
    for (let i = 0; i < maxLength; i++) {
      const group = tableDatas.map((arr) => (i < arr.length ? arr[i] : null));
      pages.push(group);
    }
    return pages;
  }, [tableData]);

  const rowHasRecords = useCallback((row) => {
    return Object.keys(row).some((key) => {
      if (typeof row[key] === 'object' && row[key] !== null) {
        return row[key].isRecord;
      }
      return false;
    });
  }, []);

  return pageData.map((page, pageIndex) => {
    const pageColumn = pageColumns[pageIndex];
    return (
      <div
        className={
          pageIndex > 0 ? 'page-break table-container' : 'table-container'
        }
      >
        <Table bordered striped responsive>
          <thead>
            <tr>
              {pageColumn.map((column) => (
                <th
                  key={column.trapId}
                  colSpan={column.colSpan ? column.colSpan : 1}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page.map((row, index) => {
              if (row === null) {
                const colSpan = Object.keys(pageColumn).reduce((acc, key) => {
                  const value = pageColumn[key];
                  return acc + (value.colSpan !== undefined ? 2 : 1);
                }, 0);
                return (
                  <tr key={index}>
                    <td colSpan={colSpan}></td>
                  </tr>
                );
              }
              const hasSprays = row.spray !== undefined;
              const hasRecords = rowHasRecords(row);
              return (
                <tr key={index}>
                  {row.date ? <td>{row.date}</td> : null}
                  {row.week ? <td>{row.week}</td> : null}
                  {hasSprays ? (
                    <Fragment>
                      <td colSpan={4 + 2 * traplogStore.traps.length}>
                        Spray Application{' '}
                        {<i>{row.spray.biofix === 1 && '(Biofix)'}</i>}:{' '}
                        <b>{row.spray.product}</b>
                        {row.spray.comments ? (
                          <>
                            <br />
                            <i>{row.spray.comments}</i>
                          </>
                        ) : null}
                      </td>
                    </Fragment>
                  ) : null}
                  {hasRecords ? (
                    <>
                      {row.capChanges ? (
                        <td>
                          {row.capChanges === 'some' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>Some caps changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-warning'
                              />
                            </OverlayTrigger>
                          ) : row.capChanges === 'all' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>All caps changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-success'
                              />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {row.baseChanges ? (
                        <td>
                          {row.baseChanges === 'some' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>Some bases changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-warning'
                              />
                            </OverlayTrigger>
                          ) : row.baseChanges === 'all' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>All bases changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-success'
                              />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {row.threshold ? (
                        <td
                          className={
                            row.threshold.sprayed ? 'text-info' : 'text-danger'
                          }
                        >
                          {row.threshold.amountThresholdsMet > 0 ||
                          row.threshold.accumulationThresholdsMet > 0 ||
                          row.threshold.averageThresholdMet ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip>
                                  {row.threshold.sprayed ? (
                                    <Row>
                                      <Col>
                                        Previous spray covers these thresholds.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.amountThresholdsMet > 0 ? (
                                    <Row>
                                      <Col>
                                        {row.threshold.amountThresholdsMet} x
                                        Single trap catch threshold.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.accumulationThresholdsMet >
                                  0 ? (
                                    <Row>
                                      <Col>
                                        {
                                          row.threshold
                                            .accumulationThresholdsMet
                                        }{' '}
                                        x accumulation threshold.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.averageThresholdMet ? (
                                    <Row>
                                      <Col>Average threshold.</Col>
                                    </Row>
                                  ) : null}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faExclamationCircle} />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {columns
                        .filter((column) => column.trapId !== undefined)
                        .filter((column) =>
                          Object.keys(row).includes(column.trapName)
                        )
                        .map((column, index) => {
                          const record = row[column.trapName];
                          return (
                            <Fragment key={index}>
                              <td
                                className={
                                  record.amountThresholdMet
                                    ? 'text-danger'
                                    : null
                                }
                              >
                                {record.amount === null ? '-' : record.amount}
                              </td>
                              <td
                                className={`${
                                  record.accumulationThresholdMet
                                    ? 'text-danger'
                                    : null
                                } ${record.comments ? 'pr-0' : null}`}
                              >
                                <i>
                                  {record.accumulation === null
                                    ? '-'
                                    : record.accumulation}
                                </i>
                                {record.comments ? (
                                  <FontAwesomeIcon
                                    className='text-primary ml-2'
                                    icon={faComment}
                                  />
                                ) : null}
                              </td>
                            </Fragment>
                          );
                        })}
                    </>
                  ) : null}
                  {row.averageAmount ? (
                    <td
                      className={
                        row.averageAmount.averageThresholdMet
                          ? 'text-danger'
                          : null
                      }
                    >
                      {row.averageAmount.averageAmount
                        ? row.averageAmount.averageAmount
                        : '-'}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  });
};

/*
 * Client view, no CRUD, can only view trapping results for one trapping zone at a time.
 */
function Traplog(props) {
  const { traplogStore } = useStores();
  const [trapsOpen, trapsHandleCollapse] = useCollapse('traplog-traps-open');

  const customOptions = props.customOptions;
  const customInsects = customOptions?.insects;

  const hasHighRisk = traplogStore.trappingZones.some((tz) => tz.high_risk);
  const hasHighPressure = traplogStore.trappingZones.some(
    (tz) => tz.high_pressure
  );

  return (
    <Fragment>
      <div className='traplog'>
        <Row className='justify-content-center'>
          {isMobile(window.navigator).any ? (
            <>
              <Col
                xs={3}
                sm={2}
                className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
              >
                Property:
              </Col>
              <Col xs={9} md={8} lg={6}>
                <GlobalPropertiesSelect
                  selectedDatabase={props.selectedDatabase}
                  bearerToken={props.bearerToken}
                  refreshBearerToken={props.refreshBearerToken}
                  propertiesStore={props.propertiesStore}
                />
              </Col>
              <Col
                xs={0}
                sm={1}
                className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
                style={{ visibility: 'hidden' }}
              >
                Property:
              </Col>
            </>
          ) : null}
          <>
            <Col
              xs={3}
              sm={2}
              className='d-flex align-items-center justify-content-end pr-0 font-weight-bold'
            >
              Insect:
            </Col>
            <Col xs={9} md={8} lg={6}>
              <InsectGroupSelect
                selectedDatabase={props.selectedDatabase}
                bearerToken={props.bearerToken}
                refreshBearerToken={props.refreshBearerToken}
                customInsects={customInsects}
              />
            </Col>
            <Col
              xs={0}
              sm={1}
              className='d-flex align-items-center justify-content-end pl-0 font-weight-bold'
              style={{ visibility: 'hidden' }}
            >
              Insect:
            </Col>
          </>
        </Row>

        {traplogStore.selectedInsectGroupId === null ? null : props
            .propertiesStore.loading ||
          traplogStore.loadingInsectGroups ||
          traplogStore.loadingTrappingZones ||
          traplogStore.loadingTrappingResults ? (
          <div className='text-center mt-3'>
            <Spinner variant='primary' animation='border' />
          </div>
        ) : (
          <Fragment>
            {hasHighRisk && (
              <Row className='mt-3'>
                <Col>
                  <HighRiskAlert />
                </Col>
              </Row>
            )}
            {hasHighPressure && (
              <Row>
                <Col>
                  <HighPressureAlert />
                </Col>
              </Row>
            )}
            <Row className='mt-3' style={{ height: '400px' }}>
              <Col>
                <PropertyOverviewMap
                  propertiesStore={props.propertiesStore}
                  onClick={(id) => {
                    traplogStore.setSelectedTrappingZoneId(id);
                  }}
                />
              </Col>
            </Row>
            <TraplogLegend className='mt-3 text-center' />

            {/* Trapping Zones */}
            <Row className='mt-3'>
              <Col className='text-center'>
                {traplogStore.trappingZones.map((trappingZone) => (
                  <TrappingZone
                    key={trappingZone.id}
                    trappingZone={trappingZone}
                  />
                ))}
              </Col>
            </Row>

            {/* Traps Table */}
            {traplogStore.selectedTrappingZoneId &&
            traplogStore.traps !== null ? (
              <Row className='my-3'>
                <Col>
                  <h3
                    onClick={trapsHandleCollapse}
                    className='text-center mb-2'
                  >
                    Traps{'\t'}
                    <FontAwesomeIcon
                      icon={trapsOpen ? faChevronDown : faChevronUp}
                      size='xs'
                    />
                  </h3>
                  <Collapse in={trapsOpen}>
                    <div>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Trap ID</th>
                            <th>Name</th>
                            <th>Location</th>
                            <th>Setup Date</th>
                            <th>Comments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {traplogStore.traps.map((trap) => (
                            <tr key={trap.id}>
                              <td>{trap.code}</td>
                              <td>{trap.name}</td>
                              <td>{trap.location_description}</td>
                              <td>
                                {moment(trap.setup_at).format('DD/MM/YYYY')}
                              </td>
                              <td>{trap.comments}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Collapse>
                </Col>
              </Row>
            ) : null}

            {/* Trapping Results Table */}
            {props.propertiesStore.selectedProperty &&
            traplogStore.selectedTrappingZoneId !== null &&
            traplogStore.trappingResults[
              traplogStore.selectedTrappingZoneId
            ] ? (
              <Row className='my-3 page-break'>
                <Col>
                  <h3 className='text-center mb-2 traplog-title'>
                    Trap Records
                  </h3>
                  <div className={'screen-only-view'}>
                    <TrappingResultsTable />
                  </div>
                  <div className={'print-only-view'}>
                    <TrappingResultsTablePrinted />
                  </div>
                </Col>
              </Row>
            ) : null}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}

Traplog.propTypes = {
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  customOptions: PropTypes.object
};

export default observer(Traplog);
